import { useState, useMemo, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Building2 } from 'lucide-react'
import { useBuildings, useDeleteBuilding, useReorderBuildings, useProperties, Building } from '@/hooks'

import { countPropertyGroups, filterBuildings, pageOf, type PropertyCounts } from '@/lib/property_groups'

type FilterStatus = 'all' | 'draft' | 'published' | 'archived'

/**
 * Buildings list page with status filtering, search, and drag-and-drop reordering.
 * Uses Convex queries with client-side filtering for search.
 */
export function Buildings() {
  const [searchParams, setSearchParams] = useSearchParams()
  const statusParam = searchParams.get('status')
  const currentStatus: FilterStatus = ['draft', 'published', 'archived'].includes(statusParam || '') ? statusParam as FilterStatus : 'all'
  const requestedPage = Number(searchParams.get('page') || '1')
  const searchQuery = searchParams.get('search') || ''
  const debouncedSearch = searchQuery
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [localBuildings, setLocalBuildings] = useState<Building[] | null>(null)

  const ITEMS_PER_PAGE = 10

  const handleSearchChange = (value: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('page', '1')
    if (value) next.set('search', value)
    else next.delete('search')
    setSearchParams(next, { replace: true })
  }

  // Fetch buildings with Convex query
  const { data, isLoading, error, refetch } = useBuildings({
    status: currentStatus === 'all' ? undefined : currentStatus,
  })

  const { data: unitData, isLoading: unitsLoading } = useProperties()
  const unitCounts = useMemo(() => countPropertyGroups(unitData ?? []), [unitData])
  const deleteMutation = useDeleteBuilding()
  const reorderMutation = useReorderBuildings()

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px drag before starting
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Use local buildings during drag, otherwise use server data
  const displayData = localBuildings ?? data

  const filteredBuildings = useMemo(() => filterBuildings(displayData ?? [], unitData ?? [], debouncedSearch), [displayData, unitData, debouncedSearch])
  const totalItems = filteredBuildings.length
  const { page: currentPage, totalPages, startIndex, items: buildings } = pageOf(filteredBuildings, requestedPage, ITEMS_PER_PAGE)
  const hasMore = currentPage < totalPages

  // Handle drag end - reorder buildings
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id || !displayData) {
        setLocalBuildings(null)
        return
      }

      const oldIndex = displayData.findIndex((b: Building) => b._id === active.id)
      const newIndex = displayData.findIndex((b: Building) => b._id === over.id)

      if (oldIndex === -1 || newIndex === -1) {
        setLocalBuildings(null)
        return
      }

      // Optimistically update local state
      const newOrder = arrayMove(displayData, oldIndex, newIndex) as Building[]
      setLocalBuildings(newOrder)

      // Prepare order items for API - update all items with new positions
      const orderItems = newOrder.map((b: Building, index: number) => ({
        id: b._id,
        sort_order: index,
      }))

      try {
        await reorderMutation.mutateAsync(orderItems)
      } catch (err) {
        console.error('Failed to reorder buildings:', err)
        // Revert to server state on error
        setLocalBuildings(null)
      } finally {
        // Clear local state after update completes
        setLocalBuildings(null)
      }
    },
    [displayData, reorderMutation]
  )

  function handleStatusChange(status: FilterStatus) {
    if (status === 'all') {
      searchParams.delete('status')
    } else {
      searchParams.set('status', status)
    }
    searchParams.set('page', '1') // Reset to first page
    setSearchParams(searchParams)
  }

  function handlePageChange(page: number) {
    searchParams.set('page', String(page))
    setSearchParams(searchParams)
  }

  async function handleDelete(id: string) {
    try {
      await deleteMutation.mutateAsync(id)
      setDeleteConfirmId(null)
    } catch (err) {
      console.error('Failed to delete building:', err)
    }
  }

  const statusTabs: { value: FilterStatus; label: string }[] = [
    { value: 'all', label: 'Todos' },
    { value: 'published', label: 'Publicados' },
    { value: 'draft', label: 'Borradores' },
    { value: 'archived', label: 'Archivados' },
  ]

  // Disable drag when searching or paginating
  const isDragDisabled = !!debouncedSearch || currentPage > 1 || currentStatus !== 'all' || reorderMutation.isPending

  // Loading state
  if (isLoading || unitsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Edificios</h1>
            <p className="mt-2 text-muted-foreground">Gestiona tus promociones y desarrollos</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Edificios</h1>
            <p className="mt-2 text-muted-foreground">Gestiona tus promociones y desarrollos</p>
          </div>
        </div>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
          <p className="text-destructive">Error al cargar edificios</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'Ocurrio un error'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Edificios</h1>
          <p className="mt-2 text-muted-foreground">
            Gestiona tus promociones y desarrollos
          </p>
        </div>
        <Link
          to="/buildings/create"
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          + Nuevo Edificio
        </Link>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border bg-card p-4"><p className="text-sm text-muted-foreground">Promociones en esta selección</p><p className="mt-1 text-2xl font-semibold">{totalItems}</p></div>
        <div className="rounded-xl border bg-card p-4"><p className="text-sm text-muted-foreground">Viviendas de estas promociones</p><p className="mt-1 text-2xl font-semibold">{filteredBuildings.reduce((sum, building) => sum + (unitCounts.get(building._id)?.total ?? 0), 0)}</p></div>
        <Link to="/properties?building=standalone" className="rounded-xl border bg-card p-4 hover:bg-muted"><p className="text-sm text-muted-foreground">Viviendas independientes</p><p className="mt-1 text-2xl font-semibold">{unitCounts.get('standalone')?.total ?? 0} <span className="text-sm font-normal">Ver viviendas →</span></p></Link>
      </div>
      <p className="text-sm text-muted-foreground">Abre las viviendas de una promoción para gestionar cada unidad. Publicación web no confirma disponibilidad ni reserva el edificio completo.</p>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Status Tabs */}
        <div className="flex gap-1 rounded-lg border bg-muted p-1">
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => handleStatusChange(tab.value)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                currentStatus === tab.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Promoción, dirección o referencia de vivienda…"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>
      </div>

      {/* Reorder hint */}
      {!isDragDisabled && buildings.length > 1 && (
        <p className="text-sm text-muted-foreground">
          Arrastra las filas para reordenar los edificios
        </p>
      )}

      {/* Buildings List */}
      {buildings.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/50 p-12 text-center">
          <Building2 className="h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">
            {debouncedSearch ? 'No hay edificios coincidentes' : 'Aun no hay edificios'}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {debouncedSearch
              ? `Ningun edificio coincide con "${debouncedSearch}"`
              : 'Comienza creando tu primera promocion.'}
          </p>
          {!debouncedSearch && (
            <Link
              to="/buildings/create"
              className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Crear Edificio
            </Link>
          )}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full">
              <thead className="border-b bg-muted/50">
                <tr>
                  {!isDragDisabled && <th className="w-10 px-2 py-3"></th>}
                  <th className="px-4 py-3 text-left text-sm font-medium">Edificio</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Publicación web</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Viviendas</th>
                  <th className="hidden px-4 py-3 text-left text-sm font-medium md:table-cell">Ubicacion</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Acciones</th>
                </tr>
              </thead>
              <SortableContext
                items={buildings.map((b: Building) => b._id)}
                strategy={verticalListSortingStrategy}
                disabled={isDragDisabled}
              >
                <tbody className="divide-y">
                  {buildings.map((building: Building) => (
                    <SortableBuildingRow
                      key={building._id}
                      building={building}
                      counts={unitCounts.get(building._id)}
                      isDragDisabled={isDragDisabled}
                      deleteConfirmId={deleteConfirmId}
                      setDeleteConfirmId={setDeleteConfirmId}
                      handleDelete={handleDelete}
                      isDeleting={deleteMutation.isPending}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </div>
        </DndContext>
      )}

      {/* Pagination */}
      {totalItems > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Mostrando {startIndex + 1}-
            {Math.min(startIndex + ITEMS_PER_PAGE, totalItems)} de{' '}
            {totalItems} edificios
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50 disabled:hover:bg-transparent"
            >
              Anterior
            </button>
            {/* Page numbers */}
            {totalPages > 1 && (
              <div className="hidden items-center gap-1 sm:flex">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum: number
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`min-w-[2rem] rounded-md px-2 py-1.5 text-sm ${
                        currentPage === pageNum
                          ? 'bg-primary text-primary-foreground'
                          : 'border hover:bg-muted'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>
            )}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasMore}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50 disabled:hover:bg-transparent"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* Mobile delete confirmation modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 sm:hidden">
          <div className="mx-4 rounded-lg bg-background p-6 shadow-lg">
            <h3 className="text-lg font-semibold">Eliminar Edificio?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Esta accion no se puede deshacer. Se eliminaran tambien todas las imagenes asociadas.
            </p>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 rounded-md border px-4 py-2 text-sm hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={deleteMutation.isPending}
                className="flex-1 rounded-md bg-destructive px-4 py-2 text-sm text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Sortable Building Row Component
// ============================================================================

interface SortableBuildingRowProps {
  building: Building
  counts?: PropertyCounts
  isDragDisabled: boolean
  deleteConfirmId: string | null
  setDeleteConfirmId: (id: string | null) => void
  handleDelete: (id: string) => void
  isDeleting: boolean
}

function SortableBuildingRow({
  building,
  counts,
  isDragDisabled,
  deleteConfirmId,
  setDeleteConfirmId,
  handleDelete,
  isDeleting,
}: SortableBuildingRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: building._id, disabled: isDragDisabled })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  }

  // Get location display - prefer city, fall back to region
  const location = [building.address, building.city || building.region].filter(Boolean).join(', ') || '-'

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-muted/50 ${isDragging ? 'bg-muted shadow-lg' : ''}`}
    >
      {/* Drag Handle */}
      {!isDragDisabled && (
        <td className="px-2 py-3">
          <button
            type="button"
            className="cursor-grab rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
            {...attributes}
            {...listeners}
            title="Arrastra para reordenar"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </td>
      )}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Building icon placeholder */}
          <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-muted">
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <Building2 className="h-6 w-6" />
            </div>
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium">{building.name}</div>
            <div className="truncate text-sm text-muted-foreground">
              {building.slug}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={building.status} />
      </td>
      <td className="px-4 py-3 text-sm">
        <Link to={`/properties?buildingId=${building._id}`} className="font-medium text-primary hover:underline">{counts?.total ?? 0} viviendas →</Link>
        <p className="text-xs text-muted-foreground">{counts?.published ?? 0} publicadas · {counts?.draft ?? 0} borradores</p>
      </td>
      <td className="hidden px-4 py-3 text-sm md:table-cell">
        {location}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/buildings/${building._id}/edit`}
            className="rounded-md px-2 py-1 text-sm text-primary hover:bg-muted"
          >
            Editar
          </Link>
          {deleteConfirmId === building._id ? (
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleDelete(building._id)}
                disabled={isDeleting}
                className="rounded-md bg-destructive px-2 py-1 text-sm text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                {isDeleting ? '...' : 'Si'}
              </button>
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-muted"
              >
                No
              </button>
            </div>
          ) : (
            <button
              onClick={() => setDeleteConfirmId(building._id)}
              className="rounded-md px-2 py-1 text-sm text-destructive hover:bg-destructive/10"
            >
              Eliminar
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

function StatusBadge({ status }: { status: 'draft' | 'published' | 'archived' }) {
  const styles = {
    draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    published: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    archived: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
  }

  const labels: Record<string, string> = {
    draft: 'Borrador',
    published: 'Publicado',
    archived: 'Archivado',
  }

  return (
    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  )
}
